/**
 * Job Similarity Matcher - V4 Target-Based Matching
 * 
 * Uses V4 data helpers to find similar analysis jobs
 * Compares vehicles, people, activities across jobs
 */

import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  getVehicles, 
  getPeople, 
  getAllTargets,
  isV4Data,
  hasLicensePlate
} from './v4DataHelpers';
import { ensureJobHasOrderId, validateAndNormalizeJob } from './jobValidation';

// Similarity scoring weights
const SIMILARITY_WEIGHTS = {
  EXACT_MATCH: 1.0,
  HIGH_SIMILARITY: 0.8,
  MEDIUM_SIMILARITY: 0.6,
  PARTIAL_MATCH: 0.4,
  LOW_SIMILARITY: 0.2,
  NO_MATCH: 0
};

/**
 * Main function to find similar jobs
 */
export const findSimilarJobs = async (currentJobId, currentResults, options = {}) => {
  const {
    maxResults = 10,
    minSimilarityScore = 0.3,
    searchRadius = 'all', // 'casefile', 'user', 'all'
    includeCurrentCasefile = false,
    onlyOtherUsers = false // when true, exclude jobs by current user
  } = options;

  try {
    console.log(`🔍 Finding similar jobs for ${currentJobId}`);
    
    // Get current job metadata
    const currentJob = await getCurrentJobMetadata(currentJobId);
    if (!currentJob) {
      console.error('Could not find current job metadata');
      return [];
    }

    // Extract features using centralized utilities
    const currentFeatures = extractComparisonFeatures(currentResults);
    if (!currentFeatures) {
      console.error('Could not extract features from current job');
      return [];
    }

    // Get candidate jobs
    const candidates = await getCandidateJobs(currentJob, searchRadius, includeCurrentCasefile, { onlyOtherUsers });
    console.log(`Found ${candidates.length} candidate jobs to compare`);

    // Compare each candidate
    const similarJobs = [];
    
    for (const candidate of candidates) {
      if (candidate.jobId === currentJobId) continue;
      
      // Get candidate results
      const candidateResults = await getCandidateResults(candidate.jobId);
      if (!candidateResults) continue;
      
      // Extract features and compare
      const candidateFeatures = extractComparisonFeatures(candidateResults);
      if (!candidateFeatures) continue;
      
      const similarityScore = calculateSimilarity(currentFeatures, candidateFeatures);
      
      if (similarityScore.total >= minSimilarityScore) {
        console.log(`✅ Job ${candidate.jobId} matches with score ${similarityScore.total}`);
        
        // Ensure orderId is present
        let orderId = candidate.orderId;
        if (!orderId) {
          orderId = await lookupOrderIdForJob(candidate.jobId);
        }
        
        if (!orderId) {
          console.log(`⚠️ Skipping job - no orderId found: ${candidate.jobId}`);
          continue;
        }
        
        // Get evidence information if available
        let evidenceFiles = [];
        if (candidate.evidenceId && candidate.casefileId) {
          try {
            const evidenceData = await getEvidenceData(candidate.evidenceId, candidate.casefileId);
            if (evidenceData) {
              evidenceFiles = [evidenceData];
            } else {
              // Fallback evidence object
              evidenceFiles = [{
                id: candidate.evidenceId,
                casefileId: candidate.casefileId,
                fileName: `Evidence ${candidate.evidenceId.slice(-8)}`,
                contentType: 'unknown'
              }];
            }
          } catch (error) {
            console.warn(`Could not fetch evidence data for ${candidate.evidenceId}:`, error);
            // Fallback evidence object
            evidenceFiles = [{
              id: candidate.evidenceId,
              casefileId: candidate.casefileId,
              fileName: `Evidence ${candidate.evidenceId.slice(-8)}`,
              contentType: 'unknown'
            }];
          }
        }

        similarJobs.push({
          jobId: candidate.jobId,
          orderId: orderId,
          casefileId: candidate.casefileId,
          evidenceId: candidate.evidenceId,
          createdAt: candidate.createdAt,
          provider: candidate.provider,
          similarityScore: similarityScore.total,
          matchDetails: similarityScore.details,
          matchTypes: similarityScore.types,
          confidence: similarityScore.confidence,
          // Include all original job data for proper card display
          selectedServices: candidate.selectedServices || [],
          selectedTargets: candidate.selectedTargets || [],
          selectedModel: candidate.selectedModel,
          serviceName: candidate.serviceName,
          analysisRequest: candidate.analysisRequest,
          userId: candidate.userId,
          status: candidate.status || 'completed',
          // Include evidence files for the evidence tab
          evidenceFiles: evidenceFiles
        });
      }
    }
    
    // Sort by similarity score
    similarJobs.sort((a, b) => b.similarityScore - a.similarityScore);
    
    return similarJobs.slice(0, maxResults);
    
  } catch (error) {
    console.error('Error finding similar jobs:', error);
    return [];
  }
};

/**
 * Extract comparison features using V4 data helpers
 */
const extractComparisonFeatures = (results) => {
  if (!results?.structuredData) {
    console.log('❌ No structured data found');
    return null;
  }

  const data = results.structuredData;
  
  // Check for V4 data
  if (!isV4Data(data)) {
    console.warn('⚠️ Similarity matching requires V4 data');
    return null;
  }
  
  // Use V4 helpers to extract targets
  const vehicles = getVehicles(data);
  const people = getPeople(data);
  const allTargets = getAllTargets(data);
  
  // Extract license plates from vehicles
  const licensePlates = vehicles
    .filter(v => hasLicensePlate(v))
    .map(v => ({
      plateNumber: v.attributes.license_plate,
      vehicleId: v.id
    }));
  
  // Extract all activities from all targets
  const activities = [];
  allTargets.forEach(target => {
    (target.actions?.activities || []).forEach(activity => {
      activities.push({
        type: activity.type,
        description: activity.description
      });
    });
  });
  
  console.log('📊 Extracted V4 features:', {
    vehicles: vehicles.length,
    licensePlates: licensePlates.length,
    people: people.length,
    activities: activities.length
  });
  
  return {
    vehicles,
    licensePlates,
    people,
    activities,
    text: [] // V4 doesn't have standalone text items
  };
};

/**
 * Calculate similarity between two feature sets
 */
const calculateSimilarity = (features1, features2) => {
  const scores = {
    vehicles: compareVehicles(features1.vehicles, features2.vehicles),
    licensePlates: compareLicensePlates(features1.licensePlates, features2.licensePlates),
    people: comparePeople(features1.people, features2.people),
    activities: compareActivities(features1.activities, features2.activities),
    text: compareText(features1.text, features2.text)
  };
  
  // Calculate weighted total
  const weights = {
    vehicles: 0.35,
    licensePlates: 0.35,
    people: 0.15,
    activities: 0.10,
    text: 0.05
  };
  
  let totalScore = 0;
  let totalWeight = 0;
  const types = [];
  const details = [];
  
  for (const [key, score] of Object.entries(scores)) {
    if (score.score > 0) {
      totalScore += score.score * weights[key];
      totalWeight += weights[key];
      types.push(...(score.types || []));
      details.push(...(score.matches || []));
    }
  }
  
  const normalizedScore = totalWeight > 0 ? totalScore / totalWeight : 0;
  
  return {
    total: normalizedScore,
    breakdown: scores,
    types: [...new Set(types)],
    details,
    confidence: normalizedScore > 0.7 ? 'high' : normalizedScore > 0.4 ? 'medium' : 'low'
  };
};

/**
 * Compare vehicles
 */
const compareVehicles = (vehicles1, vehicles2) => {
  if (!vehicles1.length || !vehicles2.length) {
    return { score: 0, matches: [], types: [] };
  }
  
  let bestScore = 0;
  const matches = [];
  const types = [];
  
  vehicles1.forEach(v1 => {
    vehicles2.forEach(v2 => {
      let score = 0;
      
      // Compare make/model
      if (v1.make === v2.make && v1.make !== 'Unknown') {
        score += SIMILARITY_WEIGHTS.MEDIUM_SIMILARITY;
        if (v1.model === v2.model && v1.model !== 'Unknown') {
          score += SIMILARITY_WEIGHTS.HIGH_SIMILARITY;
          types.push('exact_vehicle');
          matches.push(`${v1.make} ${v1.model}`);
        } else {
          types.push('same_make');
        }
      }
      
      // Compare color
      if (v1.color === v2.color && v1.color !== 'Unknown') {
        score += SIMILARITY_WEIGHTS.PARTIAL_MATCH;
      }
      
      // Compare license plates if present
      if (v1.licensePlates?.length && v2.licensePlates?.length) {
        const plate1 = v1.licensePlates[0].plateNumber;
        const plate2 = v2.licensePlates[0].plateNumber;
        if (plate1 === plate2 && plate1 !== 'UNKNOWN') {
          score = SIMILARITY_WEIGHTS.EXACT_MATCH;
          types.push('exact_plate');
          matches.push(`Plate: ${plate1}`);
        }
      }
      
      bestScore = Math.max(bestScore, score);
    });
  });
  
  return { score: bestScore, matches, types };
};

/**
 * Compare license plates
 */
const compareLicensePlates = (plates1, plates2) => {
  if (!plates1.length || !plates2.length) {
    return { score: 0, matches: [], types: [] };
  }
  
  const matches = [];
  const types = [];
  let bestScore = 0;
  
  plates1.forEach(p1 => {
    const normalized1 = (p1.plateNumber || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
    if (!normalized1 || normalized1 === 'UNKNOWN') return;
    
    plates2.forEach(p2 => {
      const normalized2 = (p2.plateNumber || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
      if (!normalized2 || normalized2 === 'UNKNOWN') return;
      
      if (normalized1 === normalized2) {
        bestScore = SIMILARITY_WEIGHTS.EXACT_MATCH;
        types.push('exact_plate');
        matches.push(p1.plateNumber);
      }
    });
  });
  
  return { score: bestScore, matches, types };
};

/**
 * Compare people
 */
const comparePeople = (people1, people2) => {
  if (!people1.length || !people2.length) {
    return { score: 0, matches: [], types: [] };
  }
  
  let matchCount = 0;
  const types = [];
  
  people1.forEach(p1 => {
    people2.forEach(p2 => {
      if (p1.gender === p2.gender && p1.gender !== 'Unknown') {
        // Simple gender match for now
        matchCount++;
        types.push('person_match');
      }
    });
  });
  
  const score = matchCount > 0 ? SIMILARITY_WEIGHTS.MEDIUM_SIMILARITY : 0;
  return { score, matches: [`${matchCount} people`], types };
};

/**
 * Compare activities
 */
const compareActivities = (activities1, activities2) => {
  if (!activities1.length || !activities2.length) {
    return { score: 0, matches: [], types: [] };
  }
  
  const matches = [];
  const types = [];
  let score = 0;
  
  activities1.forEach(a1 => {
    activities2.forEach(a2 => {
      if (a1.activity === a2.activity) {
        score = SIMILARITY_WEIGHTS.HIGH_SIMILARITY;
        matches.push(a1.activity);
        types.push('activity_match');
      }
    });
  });
  
  return { score, matches, types };
};

/**
 * Compare text
 */
const compareText = (text1, text2) => {
  if (!text1.length || !text2.length) {
    return { score: 0, matches: [], types: [] };
  }
  
  const matches = [];
  let score = 0;
  
  text1.forEach(t1 => {
    text2.forEach(t2 => {
      if (t1.text === t2.text && t1.text) {
        score = SIMILARITY_WEIGHTS.MEDIUM_SIMILARITY;
        matches.push(t1.text);
      }
    });
  });
  
  return { score, matches, types: matches.length ? ['text_match'] : [] };
};

/**
 * Get current job metadata
 */
const getCurrentJobMetadata = async (jobId) => {
  try {
    const jobDoc = await getDocs(query(
      collection(db, 'jobs'),
      where('__name__', '==', jobId),
      limit(1)
    ));
    
    if (!jobDoc.empty) {
      return { jobId, ...jobDoc.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting job metadata:', error);
    return null;
  }
};

/**
 * Get candidate jobs for comparison
 */
const getCandidateJobs = async (currentJob, searchRadius, includeCurrentCasefile, extra = {}) => {
  try {
    const { onlyOtherUsers = false } = extra || {};
    let jobsQuery;
    
    switch (searchRadius) {
      case 'casefile':
        jobsQuery = query(
          collection(db, 'jobs'),
          where('casefileId', '==', currentJob.casefileId),
          limit(50)
        );
        break;
        
      case 'user':
        jobsQuery = query(
          collection(db, 'jobs'),
          where('userId', '==', currentJob.userId),
          limit(100)
        );
        break;
        
      default: // 'all'
        jobsQuery = query(
          collection(db, 'jobs'),
          limit(200)
        );
    }
    
    const jobsSnapshot = await getDocs(jobsQuery);
    let candidates = jobsSnapshot.docs.map(doc => ({ 
      jobId: doc.id, 
      ...doc.data() 
    }));
    
    // Ensure all candidates have orderId
    const validatedCandidates = [];
    for (const candidate of candidates) {
      try {
        const jobWithOrderId = await ensureJobHasOrderId(candidate);
        const validatedJob = validateAndNormalizeJob(jobWithOrderId, { 
          isSimilarityResult: true,
          requireUserId: false 
        });
        validatedCandidates.push(validatedJob);
      } catch (error) {
        console.warn(`⚠️ Skipping invalid candidate job ${candidate.jobId}:`, error.message);
      }
    }
    
    candidates = validatedCandidates;
    
    // Filter for completed jobs
    candidates = candidates.filter(job => 
      job.status === 'completed' || job.status === 'success'
    );
    
    // Exclude current casefile if requested
    if (!includeCurrentCasefile && currentJob.casefileId) {
      candidates = candidates.filter(job => 
        job.casefileId !== currentJob.casefileId
      );
    }

    // Exclude current user's jobs if requested
    if (onlyOtherUsers && currentJob.userId) {
      candidates = candidates.filter(job => job.userId !== currentJob.userId);
    }
    
    return candidates;
    
  } catch (error) {
    console.error('Error getting candidate jobs:', error);
    return [];
  }
};

/**
 * Get analysis results for a candidate job
 */
const getCandidateResults = async (jobId) => {
  try {
    const resultsSnapshot = await getDocs(query(
      collection(db, 'analysisResults'),
      where('jobId', '==', jobId),
      limit(1)
    ));
    
    if (!resultsSnapshot.empty) {
      return resultsSnapshot.docs[0].data();
    }
    return null;
  } catch (error) {
    console.error(`Error getting results for job ${jobId}:`, error);
    return null;
  }
};

/**
 * Lookup orderId for a job
 */
const lookupOrderIdForJob = async (jobId) => {
  try {
    const orderSnapshot = await getDocs(query(
      collection(db, 'orders'),
      where('jobIds', 'array-contains', jobId),
      limit(1)
    ));
    
    if (!orderSnapshot.empty) {
      return orderSnapshot.docs[0].id;
    }
    return null;
  } catch (error) {
    console.error(`Error looking up orderId for job ${jobId}:`, error);
    return null;
  }
};

/**
 * Get evidence data for a specific evidence file
 */
const getEvidenceData = async (evidenceId, casefileId) => {
  try {
    const evidenceDoc = await getDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceId));
    
    if (evidenceDoc.exists()) {
      const data = evidenceDoc.data();
      return {
        id: evidenceId,
        casefileId: casefileId,
        fileName: data.fileName || data.name || `Evidence ${evidenceId.slice(-8)}`,
        downloadURL: data.downloadURL,
        thumbnailURL: data.thumbnailURL,
        fileSize: data.fileSize,
        contentType: data.contentType,
        filePath: data.filePath,
        createdAt: data.createdAt || data.uploadDate,
        uploadDate: data.uploadDate || data.createdAt,
        duration: data.duration,
        resolution: data.resolution,
        type: data.type,
        summary: data.summary,
        isPublic: data.isPublic,
        createdBy: data.createdBy,
        ...data
      };
    }
    return null;
  } catch (error) {
    console.error(`Error getting evidence data for ${evidenceId}:`, error);
    return null;
  }
};

export default findSimilarJobs;
